import React, { useState, useRef, useCallback } from "react"
import Modal from "react-modal"
import { X, Link, UploadCloud, AlignLeft, Paperclip, Calendar } from "styled-icons/feather"

import Button from "components/common/Button"
import Popper from "components/common/Popper"
import { MenuContainer } from "components/navigation/Menu"
import { modalStyles } from "components/modals/closeComposerModal"

import ChannelAvatarBadge from "./ChannelAvatarBadge"
import type { CreationChannel, GenerationOptions, ProfileOption, RecentCustomInstruction, SourceInput } from "../types"
import { formatFileSize } from "../utils"
import { getConnectionTypeLabel } from "../values"
import * as styles from "../styles"
import { CfCategory, ChannelSelection, toggleCategorySelection, toggleChannelSelection } from "../categories"
import { formatPlanningPill } from "../planning"
import type { PlanningDefaults, PlanningSettings } from "../planning"
import ChannelCategoryPicker from "./ChannelCategoryPicker"
import PlanningControl, { PLANNING_MENU_WIDTH, PLANNING_WEEK_MENU_ID } from "./PlanningControl"
import { InstructionsChoice, resolveInstructionsChoice } from "../customInstructions"

interface AddSourceModalProps {
  isOpen: boolean
  onClose: () => void
  onSubmit: (source: SourceInput, options: GenerationOptions) => Promise<void>
  profiles?: ProfileOption[]
  channels?: CreationChannel[]
  planningDefaults?: PlanningDefaults | null // absent until loaded; the planning line only shows once it is
  recentCustomInstructions?: RecentCustomInstruction[]
  mode: "posts" | "profile"
}

const MAX_FILE_SIZE_MB = 100
const MIN_TEXT_CHARS = 100
const MAX_TEXT_CHARS = 500_000
// The modal's overlay sits at z-index 20; the popover portals to the page root and must float above it.
const PLANNING_MENU_Z_INDEX = 30

const INSTRUCTIONS_STATUS_TEXT: Record<InstructionsChoice, string> = {
  inherit: "This batch uses the profile's instructions.",
  custom: "These instructions apply to this batch only. The profile stays unchanged.",
  none: "This batch runs without instructions. The profile stays unchanged.",
}


type Step = "source" | "profile" | "channel" | "instructions"
type ActiveSource = "url" | "file" | null


const AddSourceModal: React.FC<AddSourceModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  profiles = [],
  channels = [],
  planningDefaults = null,
  recentCustomInstructions = [],
  mode,
}) => {
  const [step, setStep] = useState<Step>("source")
  const [urlValue, setUrlValue] = useState("")
  const [textValue, setTextValue] = useState("")
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [isDragging, setIsDragging] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [selectedProfileId, setSelectedProfileId] = useState<string>("__new__")
  const [channelSelections, setChannelSelections] = useState<ChannelSelection[]>([])
  const [expandedCategoryChannelId, setExpandedCategoryChannelId] = useState<string | null>(null)
  const selectedChannelIds = channelSelections.map((selection) => selection.channelId)
  const [fileError, setFileError] = useState<string | null>(null)
  // null until the user opens the planning popover: the backend then applies its own defaults.
  const [planning, setPlanning] = useState<PlanningSettings | null>(null)
  const [isPlanningOpen, setIsPlanningOpen] = useState(false)
  const planningButtonRef = useRef<HTMLButtonElement>(null)
  // null until the user types, so the box keeps following the selected profile's instructions.
  const [instructionsDraft, setInstructionsDraft] = useState<string | null>(null)

  const fileInputRef = useRef<HTMLInputElement>(null)

  const activeSource: ActiveSource =
    urlValue.trim() ? "url"
    : (selectedFile || textValue.trim()) ? "file"
    : null

  const resetState = useCallback(() => {
    setStep("source")
    setUrlValue("")
    setTextValue("")
    setSelectedFile(null)
    setIsDragging(false)
    setIsSubmitting(false)
    setSelectedProfileId("__new__")
    setChannelSelections([])
    setFileError(null)
    setPlanning(null)
    setIsPlanningOpen(false)
    setInstructionsDraft(null)
  }, [])

  const handleClose = () => {
    resetState()
    onClose()
  }

  const validateFile = (file: File): string | null => {
    if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
      return `File is too large. Maximum size is ${MAX_FILE_SIZE_MB}MB.`
    }
    return null
  }

  const handleFileSelect = (file: File) => {
    const error = validateFile(file)
    if (error) {
      setFileError(error)
      setSelectedFile(null)
      return
    }
    setFileError(null)
    setSelectedFile(file)
    setUrlValue("")
    setTextValue("")
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (activeSource && activeSource !== "file") return
    setIsDragging(true)
  }

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)
    if (activeSource && activeSource !== "file") return

    const files = e.dataTransfer.files
    if (files.length > 0) {
      handleFileSelect(files[0])
    }
  }

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (files && files.length > 0) {
      handleFileSelect(files[0])
    }
  }

  const handleUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setUrlValue(e.target.value)
    if (e.target.value.trim()) {
      setSelectedFile(null)
      setFileError(null)
      setTextValue("")
    }
  }

  const handleTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setTextValue(e.target.value)
    if (e.target.value.trim()) {
      setUrlValue("")
      setSelectedFile(null)
      setFileError(null)
    }
  }

  const resolveSource = (): SourceInput | null => {
    const trimmedUrl = urlValue.trim()
    const trimmedText = textValue.trim()

    if (trimmedUrl) return { type: "url", url: trimmedUrl }
    if (selectedFile) return { type: "file", file: selectedFile }
    if (trimmedText) {
      const textFile = new File([trimmedText], "pasted-text.txt", { type: "text/plain" })
      return { type: "file", file: textFile }
    }
    return null
  }

  const trimmedTextLength = textValue.trim().length
  const isTextTooShort = trimmedTextLength > 0 && trimmedTextLength < MIN_TEXT_CHARS
  const isTextTooLong = trimmedTextLength > MAX_TEXT_CHARS
  const isTextValid = trimmedTextLength >= MIN_TEXT_CHARS && trimmedTextLength <= MAX_TEXT_CHARS

  const hasSource = (): boolean => {
    const source = resolveSource()
    if (!source) return false
    if (trimmedTextLength > 0) return isTextValid
    return true
  }

  const resolveProfileId = (): string | undefined =>
    selectedProfileId !== "__new__" ? selectedProfileId : undefined

  const profileInstructions = profiles.find((profile) => profile.id === selectedProfileId)?.customInstructions ?? ""
  const instructionsText = instructionsDraft ?? profileInstructions

  // Always sent, empty included: the endpoints require the field rather than inferring it.
  const resolveChannelSelections = (): ChannelSelection[] => channelSelections

  const toggleChannel = (channelId: string) => {
    setChannelSelections((previous) => toggleChannelSelection(previous, channelId))
    if (expandedCategoryChannelId === channelId) setExpandedCategoryChannelId(null)
  }

  // Nothing exists yet for this batch, so turning the last category off simply deselects the channel.
  const toggleChannelCategory = (channelId: string, category: CfCategory) => {
    setChannelSelections((previous) => toggleCategorySelection(previous, channelId, category))
  }

  const togglePlanning = () => {
    if (!planningDefaults) return
    setPlanning((current) => current ?? { postingDays: planningDefaults.postingDays, firstMonday: planningDefaults.firstMonday })
    setIsPlanningOpen((open) => !open)
  }

  const submitWithSource = async () => {
    const source = resolveSource()
    if (!source || isSubmitting) return
    setIsSubmitting(true)

    try {
      await onSubmit(source, {
        profileId: resolveProfileId(),
        channelSelections: resolveChannelSelections(),
        planning: planning ?? undefined,
        customInstructions: mode === "posts" ? instructionsText : undefined,
      })
      handleClose()
    } catch {
      setIsSubmitting(false)
    }
  }

  const handleContinue = () => {
    if (!hasSource()) return

    // In "profile" mode there is no profile selection step
    if (mode === "profile") {
      submitWithSource()
      return
    }

    setStep("profile")
  }

  // The URL "Add" button is a shortcut that skips the Continue button
  const handleUrlAdd = () => {
    if (!urlValue.trim()) return

    if (mode === "profile") {
      submitWithSource()
    } else {
      setStep("profile")
    }
  }

  const reactModalStyles = modalStyles(700)
  reactModalStyles.overlay.zIndex = 20
  reactModalStyles.content.overflow = "visible"

  const isUrlDisabled = activeSource !== null && activeSource !== "url"
  const isFileDisabled = activeSource !== null && activeSource !== "file"
  const isTextDisabled = isFileDisabled

  const renderSourceStep = () => {
    const title = mode === "posts" ? "Add Sources" : "Create New Profile"

    return (
      <>
        <styles.SourceModalHeader>
          <styles.SourceModalTitle>{title}</styles.SourceModalTitle>
          <styles.SourceModalSubtitle>
            Select the content you want Willow to process and learn from.
          </styles.SourceModalSubtitle>
        </styles.SourceModalHeader>

        <styles.SourceSectionsStack>
          <styles.SourceCard $disabled={isUrlDisabled}>
            <styles.SourceCardHeader>
              <styles.SourceCardIcon $color="blue">
                <Link size={18} />
              </styles.SourceCardIcon>
              <styles.SourceCardTitle>Add a URL</styles.SourceCardTitle>
            </styles.SourceCardHeader>
            <styles.SourceUrlRow>
              <styles.SourceUrlInput
                type="text"
                value={urlValue}
                onChange={handleUrlChange}
                placeholder="https://example.com/article"
                onKeyDown={e => { if (e.key === "Enter") handleUrlAdd() }}
                disabled={isUrlDisabled}
              />
              <styles.SourceUrlAddButton
                onClick={handleUrlAdd}
                disabled={!urlValue.trim() || isSubmitting || isUrlDisabled}
              >
                Add
              </styles.SourceUrlAddButton>
            </styles.SourceUrlRow>
          </styles.SourceCard>

          <styles.DropZone
            $isDragging={isDragging}
            $hasFile={!!selectedFile}
            $disabled={isFileDisabled}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => !selectedFile && !isFileDisabled && fileInputRef.current?.click()}
          >
            {selectedFile ? (
              <styles.SelectedFileInfo>
                <styles.DropZoneIconCircle>
                  <UploadCloud size={24} />
                </styles.DropZoneIconCircle>
                <styles.SelectedFileName>{selectedFile.name}</styles.SelectedFileName>
                <styles.SelectedFileSize>
                  {formatFileSize(selectedFile.size)}
                </styles.SelectedFileSize>
                <styles.ChangeFileLink
                  onClick={e => {
                    e.stopPropagation()
                    setSelectedFile(null)
                    setFileError(null)
                  }}
                >
                  Remove
                </styles.ChangeFileLink>
              </styles.SelectedFileInfo>
            ) : (
              <styles.DropZoneContent>
                <styles.DropZoneIconCircle>
                  <UploadCloud size={24} />
                </styles.DropZoneIconCircle>
                <styles.DropZoneTitle>Drag & Drop File</styles.DropZoneTitle>
                <styles.DropZoneHint>
                  PDF, DOCX, PPTX, XLSX, images, and more — up to {MAX_FILE_SIZE_MB}MB
                </styles.DropZoneHint>
                {!isFileDisabled && (
                  <styles.ChooseFilesButton
                    onClick={e => {
                      e.stopPropagation()
                      fileInputRef.current?.click()
                    }}
                  >
                    <Paperclip size={16} />
                    Choose Files
                  </styles.ChooseFilesButton>
                )}
              </styles.DropZoneContent>
            )}
          </styles.DropZone>
          <input
            ref={fileInputRef}
            type="file"
            style={{ display: "none" }}
            onChange={handleFileInputChange}
          />
          {fileError && (
            <styles.FileErrorText>{fileError}</styles.FileErrorText>
          )}

          <styles.SourceCard $disabled={isTextDisabled}>
            <styles.SourceCardHeader>
              <styles.SourceCardIcon $color="muted">
                <AlignLeft size={18} />
              </styles.SourceCardIcon>
              <styles.SourceCardTitle>Paste Text</styles.SourceCardTitle>
            </styles.SourceCardHeader>
            <styles.SourceTextArea
              value={textValue}
              onChange={handleTextChange}
              placeholder="Enter or paste your text content here..."
              rows={3}
              disabled={isTextDisabled}
              maxLength={MAX_TEXT_CHARS}
            />
            {trimmedTextLength > 0 && (
              <styles.TextCharCount $error={isTextTooShort || isTextTooLong}>
                {trimmedTextLength.toLocaleString()} / {MAX_TEXT_CHARS.toLocaleString()} characters
                {isTextTooShort && ` (minimum ${MIN_TEXT_CHARS})`}
              </styles.TextCharCount>
            )}
          </styles.SourceCard>
        </styles.SourceSectionsStack>

        <styles.SourceFooter>
          <div />
          <styles.SourceFooterActions>
            <Button variant="secondary" size="sm" onClick={handleClose} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button size="sm" onClick={handleContinue} disabled={!hasSource() || isSubmitting}>
              Continue
            </Button>
          </styles.SourceFooterActions>
        </styles.SourceFooter>
      </>
    )
  }

  const renderProfileStep = () => (
    <>
      <styles.SourceModalHeader>
        <styles.SourceModalTitle>Select a Profile</styles.SourceModalTitle>
        <styles.SourceModalSubtitle>
          For which company should the posts be created?
        </styles.SourceModalSubtitle>
      </styles.SourceModalHeader>

      <styles.ProfileList>
        <styles.ProfileListItem
          $isSelected={selectedProfileId === "__new__"}
          onClick={() => setSelectedProfileId("__new__")}
        >
          <styles.ProfileRadio $isSelected={selectedProfileId === "__new__"}>
            {selectedProfileId === "__new__" && <styles.ProfileRadioDot />}
          </styles.ProfileRadio>
          <styles.ProfileListItemContent>
            <styles.ProfileListItemName>Create new profile from this source</styles.ProfileListItemName>
            <styles.ProfileListItemDetail>
              A new company profile will be generated based on the content you provided.
            </styles.ProfileListItemDetail>
          </styles.ProfileListItemContent>
        </styles.ProfileListItem>

        {profiles.map(p => (
          <styles.ProfileListItem
            key={p.id}
            $isSelected={selectedProfileId === p.id}
            onClick={() => setSelectedProfileId(p.id)}
          >
            <styles.ProfileRadio $isSelected={selectedProfileId === p.id}>
              {selectedProfileId === p.id && <styles.ProfileRadioDot />}
            </styles.ProfileRadio>
            <styles.ProfileListItemContent>
              <styles.ProfileListItemName>{p.name}</styles.ProfileListItemName>
              <styles.ProfileListItemDetail>{p.createdAt}</styles.ProfileListItemDetail>
            </styles.ProfileListItemContent>
          </styles.ProfileListItem>
        ))}
      </styles.ProfileList>

      <styles.SourceFooter>
        <div />
        <styles.SourceFooterActions>
          <Button variant="secondary" size="sm" onClick={() => setStep("source")} disabled={isSubmitting}>
            Back
          </Button>
          <Button size="sm" onClick={() => setStep("channel")} disabled={isSubmitting}>
            Continue
          </Button>
        </styles.SourceFooterActions>
      </styles.SourceFooter>
    </>
  )

  const renderChannelStep = () => (
    <>
      <styles.SourceModalHeader>
        <styles.SourceModalTitle>Select Channels</styles.SourceModalTitle>
        <styles.SourceModalSubtitle>
          Where should these posts be published? Every post goes out on all selected channels. You can decide later instead.
        </styles.SourceModalSubtitle>
      </styles.SourceModalHeader>

      <styles.ProfileList>
        <styles.ProfileListItem
          $isSelected={selectedChannelIds.length === 0}
          onClick={() => setChannelSelections([])}
          data-testid="channel-option-none"
        >
          <styles.ProfileRadio $isSelected={selectedChannelIds.length === 0}>
            {selectedChannelIds.length === 0 && <styles.ProfileRadioDot />}
          </styles.ProfileRadio>
          <styles.ProfileListItemContent>
            <styles.ProfileListItemName>Decide later</styles.ProfileListItemName>
            <styles.ProfileListItemDetail>
              Posts won't be checked against a channel's history for duplicates.
            </styles.ProfileListItemDetail>
          </styles.ProfileListItemContent>
        </styles.ProfileListItem>

        {channels.map(channel => {
          const selection = channelSelections.find((candidate) => candidate.channelId === channel.id)
          const isSelected = selection !== undefined
          const isCategoryDrawerOpen = isSelected && expandedCategoryChannelId === channel.id
          return (
            <ChannelCategoryPicker
              key={channel.id}
              variant="card"
              selection={selection}
              isOpen={isCategoryDrawerOpen}
              testIdPrefix={channel.id}
              onToggleOpen={() => setExpandedCategoryChannelId(isCategoryDrawerOpen ? null : channel.id)}
              onToggleCategory={(category) => toggleChannelCategory(channel.id, category)}
            >
              <styles.CreationChannelItem
                onClick={() => toggleChannel(channel.id)}
                data-testid={`channel-option-${channel.id}`}
              >
                <styles.ChannelMenuCheckbox
                  type="checkbox"
                  checked={isSelected}
                  readOnly
                  tabIndex={-1}
                  data-testid={`channel-option-checkbox-${channel.id}`}
                />
                <ChannelAvatarBadge channel={channel} />
                <styles.ProfileListItemContent>
                  <styles.ProfileListItemName>{channel.username}</styles.ProfileListItemName>
                  <styles.ProfileListItemDetail>{getConnectionTypeLabel(channel)}</styles.ProfileListItemDetail>
                </styles.ProfileListItemContent>
              </styles.CreationChannelItem>
            </ChannelCategoryPicker>
          )
        })}
      </styles.ProfileList>

      <styles.SourceFooter>
        {planningDefaults ? (
          <styles.PlanningPill
            ref={planningButtonRef}
            type="button"
            onClick={togglePlanning}
            aria-expanded={isPlanningOpen}
            data-testid="creation-planning-pill"
          >
            <Calendar size={15} />
            {formatPlanningPill(planning ?? planningDefaults)}
          </styles.PlanningPill>
        ) : (
          <div />
        )}
        <styles.SourceFooterActions>
          <Button variant="secondary" size="sm" onClick={() => setStep("profile")} disabled={isSubmitting}>
            Back
          </Button>
          <Button size="sm" onClick={() => setStep("instructions")} disabled={isSubmitting}>
            Continue
          </Button>
        </styles.SourceFooterActions>
      </styles.SourceFooter>

      <Popper
        referenceElement={planningButtonRef.current}
        visible={isPlanningOpen && !!planning && !!planningDefaults}
        placement="top-start"
        onClose={() => setIsPlanningOpen(false)}
        exceptions={planningButtonRef.current ? [planningButtonRef.current, PLANNING_WEEK_MENU_ID] : [PLANNING_WEEK_MENU_ID]}
        offset={[0, 8]}
        zIndex={PLANNING_MENU_Z_INDEX}
      >
        <MenuContainer width={PLANNING_MENU_WIDTH}>
          <styles.PlanningPopoverBody>
            {planning && planningDefaults && (
              <PlanningControl value={planning} defaults={planningDefaults} onChange={setPlanning} testIdPrefix="creation" />
            )}
            <styles.PlanningActions>
              <Button size="sm" onClick={() => setIsPlanningOpen(false)} data-testid="creation-planning-done">
                Done
              </Button>
            </styles.PlanningActions>
          </styles.PlanningPopoverBody>
        </MenuContainer>
      </Popper>
    </>
  )

  const renderInstructionsStep = () => {
    const choice = resolveInstructionsChoice(instructionsText, profileInstructions)
    const hasProfileInstructions = profileInstructions.trim().length > 0
    const showStatus = hasProfileInstructions || choice !== "inherit"

    return (
      <>
        <styles.SourceModalHeader>
          <styles.SourceModalTitle>Custom Instructions</styles.SourceModalTitle>
          <styles.SourceModalSubtitle>
            Tell Create what to pay attention to.
          </styles.SourceModalSubtitle>
        </styles.SourceModalHeader>

        <styles.SourceSectionsStack>
          <div>
            <styles.InstructionsTextArea
              value={instructionsText}
              onChange={(event) => setInstructionsDraft(event.target.value)}
              placeholder={'For example: "Focus on our spring launch" or "Keep every post under 800 characters."'}
              rows={6}
              aria-label="Instructions for this batch"
              data-testid="batch-instructions-input"
            />
            <styles.InstructionsStatusRow>
              <styles.InstructionsStatusText data-testid="batch-instructions-status">
                {showStatus ? INSTRUCTIONS_STATUS_TEXT[choice] : ""}
              </styles.InstructionsStatusText>
              {hasProfileInstructions && (
                // Kept in the layout while hidden so the row doesn't jump on the first keystroke.
                <Button
                  variant="secondary"
                  size="sm"
                  flexShrink={0}
                  onClick={() => setInstructionsDraft(null)}
                  disabled={isSubmitting}
                  style={{ visibility: choice === "inherit" ? "hidden" : "visible" }}
                >
                  Reset
                </Button>
              )}
            </styles.InstructionsStatusRow>
          </div>

          {recentCustomInstructions.length > 0 && (
            <styles.RecentInstructionsCard data-testid="recent-instructions">
              <styles.SourceCardHeader>
                <styles.SourceCardTitle>Previous custom instructions used</styles.SourceCardTitle>
              </styles.SourceCardHeader>
              <styles.RecentInstructionsList>
                {recentCustomInstructions.map((recent) => (
                  <styles.RecentInstructionItem key={recent.text}>
                    <styles.RecentInstructionText title={recent.text}>{recent.text}</styles.RecentInstructionText>
                    <styles.RecentUseButton type="button" aria-label="Use these instructions" title="Use these instructions" onClick={() => setInstructionsDraft(recent.text)}>
                      Use
                    </styles.RecentUseButton>
                  </styles.RecentInstructionItem>
                ))}
              </styles.RecentInstructionsList>
            </styles.RecentInstructionsCard>
          )}
        </styles.SourceSectionsStack>

        <styles.SourceFooter>
          <div />
          <styles.SourceFooterActions>
            <Button variant="secondary" size="sm" onClick={() => setStep("channel")} disabled={isSubmitting}>
              Back
            </Button>
            <Button size="sm" onClick={submitWithSource} disabled={isSubmitting}>
              {isSubmitting ? "Processing..." : "Generate Posts"}
            </Button>
          </styles.SourceFooterActions>
        </styles.SourceFooter>
      </>
    )
  }

  return (
    <Modal
      ariaHideApp={false}
      isOpen={isOpen}
      // @ts-ignore
      style={reactModalStyles}
      onRequestClose={handleClose}
    >
      <styles.SourceModalContainer>
        <styles.SourceModalCloseButton onClick={handleClose}>
          <X size={20} />
        </styles.SourceModalCloseButton>

        {step === "source"
          ? renderSourceStep()
          : step === "profile"
            ? renderProfileStep()
            : step === "channel"
              ? renderChannelStep()
              : renderInstructionsStep()}
      </styles.SourceModalContainer>
    </Modal>
  )
}

export default AddSourceModal
